import React, { useEffect, useState, useCallback } from 'react';
import { inboxApi } from '../api';
import { toast } from 'sonner';
import {
  Inbox as InboxIcon, RefreshCw, Mail, MailOpen, Loader2,
  ArrowLeft, Send, ChevronLeft, ChevronRight, Reply
} from 'lucide-react';

export default function Inbox() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [selected, setSelected] = useState(null);

  // Reply
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (filter) params.is_read = filter;
      const data = await inboxApi.getAll(params);
      setMessages(data.messages);
      setTotal(data.total);
      setUnread(data.unread);
    } catch { toast.error('Failed to load inbox'); }
    finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);
  useEffect(() => {
    const interval = setInterval(fetchInbox, 180000);
    return () => clearInterval(interval);
  }, [fetchInbox]);

  const handleCheckNow = async () => {
    setChecking(true);
    try { const res = await inboxApi.checkNow(); toast.success(res.message); fetchInbox(); }
    catch { toast.error('Check failed'); }
    finally { setChecking(false); }
  };

  const handleSelect = async (msg) => {
    setSelected(msg);
    setShowReply(false);
    setReplyText('');
    if (!msg.is_read) {
      await inboxApi.markRead(msg.id);
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
      setUnread(prev => Math.max(0, prev - 1));
    }
  };

  const handleToggleRead = async (msg, e) => {
    e.stopPropagation();
    if (msg.is_read) {
      await inboxApi.markUnread(msg.id);
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: false } : m));
      setUnread(prev => prev + 1);
    } else {
      await inboxApi.markRead(msg.id);
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: true } : m));
      setUnread(prev => Math.max(0, prev - 1));
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) { toast.error('Reply cannot be empty'); return; }
    setSending(true);
    try {
      const res = await inboxApi.reply(selected.id, replyText.trim());
      toast.success(`Reply sent to ${res.to}`);
      setShowReply(false);
      setReplyText('');
      fetchInbox();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally { setSending(false); }
  };

  const totalPages = Math.ceil(total / 30);
  const fmt = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    if (date.toDateString() === now.toDateString()) return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };
  const getInitial = (name, email) => (name || email || '?').charAt(0).toUpperCase();
  const getColor = (email) => {
    const colors = ['bg-[#1A73E8]', 'bg-[#1E8E3E]', 'bg-[#D93025]', 'bg-[#F9AB00]', 'bg-[#7C3AED]', 'bg-[#0891B2]', 'bg-[#6366F1]', 'bg-[#EA580C]'];
    let hash = 0;
    for (let i = 0; i < (email || '').length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  // Reading pane
  if (selected) {
    return (
      <div data-testid="inbox-reading-pane">
        <div className="flex items-center gap-2 mb-4">
          <button onClick={() => { setSelected(null); setShowReply(false); }} className="p-2 hover:bg-[#F1F3F4] rounded-full" data-testid="inbox-back-btn">
            <ArrowLeft className="w-5 h-5 text-[#5F6368]" />
          </button>
          <h2 className="text-lg font-semibold text-[#202124] flex-1 truncate">{selected.subject || '(no subject)'}</h2>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="p-5 border-b border-[#F1F3F4]">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm ${getColor(selected.from_email)}`}>
                {getInitial(selected.from_name, selected.from_email)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-[#202124]">{selected.from_name || selected.from_email}</span>
                  <span className="text-xs text-[#5F6368]">&lt;{selected.from_email}&gt;</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#5F6368] mt-0.5">
                  <span>to {selected.to_email}</span>
                  <span>{fmt(selected.received_at)}</span>
                </div>
              </div>
              {selected.campaign_id && <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[#E8F0FE] text-[#1A73E8]">Campaign</span>}
            </div>
          </div>
          {/* Body */}
          <div className="p-6">
            <div className="text-sm text-[#202124] leading-relaxed whitespace-pre-wrap max-h-[350px] overflow-y-auto">{selected.body || '(empty)'}</div>
          </div>
          {/* Reply section */}
          <div className="border-t border-[#F1F3F4] p-4">
            {!showReply ? (
              <button onClick={() => setShowReply(true)} className="flex items-center gap-2 px-5 py-2.5 bg-white border border-[#E2E8F0] hover:bg-[#F1F3F4] text-[#202124] rounded-full text-sm font-medium transition-colors shadow-sm" data-testid="reply-btn">
                <Reply className="w-4 h-4 text-[#1A73E8]" /> Reply
              </button>
            ) : (
              <div>
                <div className="mb-2 text-xs text-[#5F6368]">
                  Replying to <span className="font-semibold text-[#202124]">{selected.from_name || selected.from_email}</span> via <span className="font-semibold text-[#1A73E8]">{selected.to_email}</span>
                </div>
                <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={5}
                  placeholder="Write your reply..."
                  className="w-full bg-[#F8F9FA] border border-[#E2E8F0] rounded-xl px-4 py-3 text-sm text-[#202124] focus:outline-none focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20 resize-y" autoFocus data-testid="reply-textarea" />
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={handleReply} disabled={sending || !replyText.trim()}
                    className="flex items-center gap-2 px-5 py-2 bg-[#1A73E8] hover:bg-[#174EA6] text-white rounded-full text-sm font-medium disabled:opacity-50 shadow-sm" data-testid="send-reply-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
                  </button>
                  <button onClick={() => { setShowReply(false); setReplyText(''); }}
                    className="px-4 py-2 text-sm text-[#5F6368] hover:bg-[#F1F3F4] rounded-full">Discard</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="outreach-inbox">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#202124] flex items-center gap-2">
            Inbox {unread > 0 && <span className="bg-[#D93025] text-white text-xs rounded-full px-2 py-0.5 font-semibold">{unread}</span>}
          </h1>
          <p className="text-sm text-[#5F6368]">Unified inbox — all replies from all email accounts</p>
        </div>
        <button onClick={handleCheckNow} disabled={checking}
          className="flex items-center gap-1.5 px-4 py-2 bg-white border border-[#E2E8F0] hover:bg-[#F1F3F4] text-[#202124] rounded-full text-sm font-medium disabled:opacity-50 shadow-sm" data-testid="check-now-btn">
          {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 text-[#5F6368]" />} Check Now
        </button>
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-[#E2E8F0] rounded-t-2xl px-4 py-2 flex items-center gap-2">
        {[{ value: '', label: 'All' }, { value: 'false', label: 'Unread' }, { value: 'true', label: 'Read' }].map(f => (
          <button key={f.value} onClick={() => { setFilter(f.value); setPage(1); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f.value ? 'bg-[#D3E3FD] text-[#041E49]' : 'text-[#5F6368] hover:bg-[#F1F3F4]'}`}>{f.label}</button>
        ))}
        <div className="flex-1" />
        {totalPages > 1 && (
          <div className="flex items-center gap-1 text-xs text-[#5F6368]">
            <span>{(page - 1) * 30 + 1}-{Math.min(page * 30, total)} of {total}</span>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1 rounded hover:bg-[#F1F3F4] disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1 rounded hover:bg-[#F1F3F4] disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="bg-white border-x border-b border-[#E2E8F0] rounded-b-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#1A73E8] mx-auto" /></div>
        ) : messages.length === 0 ? (
          <div className="p-16 text-center">
            <InboxIcon className="w-12 h-12 mx-auto mb-3 text-[#9AA0A6]" />
            <p className="text-base text-[#5F6368] font-medium">No messages yet</p>
            <p className="text-sm text-[#9AA0A6] mt-1">Click "Check Now" to fetch replies from all accounts</p>
          </div>
        ) : (
          <div className="divide-y divide-[#F1F3F4]">
            {messages.map((msg) => (
              <div key={msg.id} onClick={() => handleSelect(msg)}
                className={`group flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors hover:bg-[#F8F9FA] hover:shadow-[inset_3px_0_0_#1A73E8] ${!msg.is_read ? 'bg-[#F2F6FC]' : ''} ${msg.is_sent ? 'bg-[#FAFAFA]' : ''}`}
                data-testid={`inbox-msg-${msg.id}`}>
                <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-semibold ${getColor(msg.is_sent ? msg.to_email : msg.from_email)}`}>
                  {getInitial(msg.is_sent ? null : msg.from_name, msg.is_sent ? msg.to_email : msg.from_email)}
                </div>
                <button onClick={(e) => handleToggleRead(msg, e)} className="flex-shrink-0 text-[#9AA0A6] hover:text-[#1A73E8]">
                  {msg.is_read ? <MailOpen className="w-4 h-4" /> : <Mail className="w-4 h-4 text-[#1A73E8]" />}
                </button>
                <div className="flex-1 min-w-0 flex items-center gap-3">
                  <span className={`text-sm w-[180px] flex-shrink-0 truncate ${!msg.is_read && !msg.is_sent ? 'text-[#202124] font-semibold' : 'text-[#5F6368]'}`}>
                    {msg.is_sent ? `To: ${msg.to_email}` : (msg.from_name || msg.from_email)}
                  </span>
                  <span className={`text-sm truncate flex-1 ${!msg.is_read ? 'text-[#202124]' : 'text-[#5F6368]'}`}>
                    {msg.is_sent && <span className="text-[#1E8E3E] font-medium mr-1">Sent</span>}
                    <span className={!msg.is_read ? 'font-semibold' : ''}>{msg.subject || '(no subject)'}</span>
                    <span className="text-[#9AA0A6]"> — {msg.snippet}</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {msg.campaign_id && <span className="text-[10px] font-semibold text-[#1A73E8] bg-[#E8F0FE] px-1.5 py-0.5 rounded">Campaign</span>}
                  <span className={`text-xs ${!msg.is_read ? 'text-[#202124] font-semibold' : 'text-[#5F6368]'}`}>{fmt(msg.received_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
